import {UI} from "../../../stem-core/ui/UIBase";
import {ConfirmationModal} from "../../../blinkpay/ui/ConfirmationModal";
import {BlinkInput} from "../../../blinkpay/ui/Input";
import {DropdownInput} from "../../../core/ui/input/dropdown/DropdownInput.jsx";
import {Button} from "../../../stem-core/ui/button/Button";
import {wrapInSpinner} from "../../../core/ui/LoadingSpinner.jsx";
import {NOOP_FUNCTION} from "../../../stem-core/base/Utils";
import {formatError} from "../../../stem-core/base/Formatting";
import {apiClient} from "../../../client/connection/BlinkApiClient.js";
import {MerchantUser, apiMerchantMergeUsers} from "../../../client/state/merchant/MerchantUserStore";
import {Toast} from "../../../blinkpay/ui/Toast.jsx";


export class MerchantMergeUserModal extends ConfirmationModal {
    inputRefs = {};
    otherMerchantUser = null;
    confirmButtonDisabled = true; // Nothing to merge until a user is found

    getDefaultOptions() {
        return {
            ...super.getDefaultOptions(),
            title: "Merge users",
            confirmLabel: "Merge",
            onSuccess: NOOP_FUNCTION,
            confirmAction: () => this.mergeUsers(),
        };
    }

    @wrapInSpinner
    async findOtherUser() {
        const query = this.inputRefs.searchInput.getValue().trim();
        if (!query) {
            return;
        }
        const isEmail = query.includes("@");
        const userFilter = isEmail
            ? {type: "users", nameOrEmailSearch: query}
            : {type: "users", userIds: [query]};
        try {
            const response = await apiClient.get("/merchant/get_users", {
                userFilter,
                pageSize: 5,
            });
            const loaded = MerchantUser.load(response);
            const candidates = isEmail
                ? loaded.filter(mu => mu.getEmail()?.toLowerCase() === query.toLowerCase())
                : loaded.filter(mu => String(mu.userId) === query);
            if (candidates.length !== 1) {
                Toast.showError(`No unique user found for ${query}`);
                return;
            }
            this.otherMerchantUser = candidates[0];
            this.confirmButtonDisabled = false;
            this.redraw();
        } catch (error) {
            Toast.showError(`Lookup failed: ${formatError(error)}`);
        }
    }

    @wrapInSpinner
    async mergeUsers() {
        const {merchantUser} = this.options;
        const finalEmail = this.finalEmailSelector?.getValue();
        let response;
        try {
            response = await apiMerchantMergeUsers(merchantUser, this.otherMerchantUser, finalEmail);
        } catch (error) {
            Toast.showError(`Could not merge users: ${formatError(error)}`);
            return;
        }
        // The response contains the survivor's full user data.
        this.options.onSuccess(response);
        this.hide();
    }

    renderFinderPhase() {
        return [
            <div style={{marginBottom: 12}}>
                Find the user to merge into <strong>{this.options.merchantUser.getEmail()}</strong>.
            </div>,
            <BlinkInput ref={{parent: this.inputRefs, name: "searchInput"}}
                        label="Email or ID of the other user"
                        inputAttributes={{placeholder: "user@example.com or 1279…"}}
            />,
            <Button onClick={() => this.findOtherUser()} label="Find" />,
        ];
    }

    renderConfirmPhase() {
        const {merchantUser} = this.options;
        const currentEmail = merchantUser.getEmail().toLowerCase();
        const otherEmail = this.otherMerchantUser.getEmail().toLowerCase();
        const olderUser = merchantUser.createdAt <= this.otherMerchantUser.createdAt ? merchantUser : this.otherMerchantUser;
        const olderEmail = olderUser.getEmail().toLowerCase();
        // Emails are stored lowercase; if the two collapse to the same string there's nothing to pick.
        const emailOptions = currentEmail === otherEmail ? [currentEmail] : [currentEmail, otherEmail];
        return [
            <div style={{marginBottom: 12}}>
                Merging <strong>{otherEmail}</strong> with <strong>{currentEmail}</strong>.
                Data and recurring payments are merged.
            </div>,
            emailOptions.length === 1
                ? <div>Primary email after merge: <strong>{emailOptions[0]}</strong></div>
                : <DropdownInput ref="finalEmailSelector"
                                 label="Primary email after merge"
                                 initialValue={olderEmail}
                                 options={emailOptions} />,
        ];
    }

    render() {
        return <div>
            {this.otherMerchantUser ? this.renderConfirmPhase() : this.renderFinderPhase()}
        </div>
    }
}